import moment from "moment";
import { Property } from "./Definition";
import { IDimension, IMeasure } from "./Query";
import { isEntity, isLookupProperty, isProperty, isStats, isTimestampProperty } from "./utils";

export interface IPlotProviderData {
    x: any;
    y: any;
    color: any,
    meta: any;
}

export interface IDataProvider {
    getData(): IPlotProviderData[];
    dimensionTitle(): string;
    measureTitle(): string;
    colorTitle(): string;
}

// Base chart data prodicer. The class manages the conversion of data
// from an dw api query result to data used in plots.
//
abstract class BaseDimensionDataProvider implements IDataProvider {
    dataFrame: any;
    dimensionConfig: any;
    measureConfig: any;
    colorConfig: any;

    dimensionField:string = '';
    dimensionMetaField:string = '';
    measureField:string = '';
    colorField:string = '';
 
    constructor(dimensionConfig: any, measureConfig: any, colorConfig: any, dataFrame: any) {
        this.dimensionConfig = dimensionConfig;
        this.measureConfig = measureConfig;
        this.colorConfig = colorConfig;
        this.dataFrame = dataFrame;
    }

    getData(): IPlotProviderData[] {
        let data: IPlotProviderData[] = [];

        this.dataFrame.toCollection(true).forEach((row:any) => {
            data.push({
                x: this.getDimensionValue(row),
                y: this.getMeasureValue(row),
                color: this.getColorValue(row),
                meta: this.getDimensionMetaValue(row),
            });
        });

        return data;
    }

    dimensionTitle(): string {
        if(isProperty(this.dimensionConfig.source.item)) {
            if(isStats(this.dimensionConfig.source.item)) {
                return this.convertToTitle(`${this.dimensionConfig.source.item!.name}_${this.dimensionConfig.source.sub}`);
            } else {
                if(this.dimensionConfig.aggregation) {
                    return this.convertToTitle(`${this.dimensionConfig.source.item!.name}_${this.dimensionConfig.aggregation}`);
                } else {
                    return this.convertToTitle(this.dimensionConfig.source.item!.name);
                }
            }
        }

        return this.convertToTitle(this.dimensionField);
    }

    measureTitle(): string {
        if(isProperty(this.measureConfig.source.item)) {
            if(isStats(this.measureConfig.source.item)) {
                return this.convertToTitle(`${this.measureConfig.source.item!.name}_${this.measureConfig.source.sub}`);
            } else {
                if(this.measureConfig.aggregation) {
                    return this.convertToTitle(`${this.measureConfig.source.item!.name}_${this.measureConfig.aggregation}`);
                } else {
                    return this.convertToTitle(this.measureConfig.source.item!.name);
                }
            }
        }

        return this.convertToTitle(this.measureField);
    }

    colorTitle(): string {
        return this.convertToTitle(this.colorField);
    }

    getDimensionValue(row:any) : any {
        if (row.hasOwnProperty(this.dimensionField)){
            return row.get(this.dimensionField);
        }else{
            return row.get(this.dimensionField.replace("_date", "_time"));
        }
    }

    getDimensionMetaValue(row:any) : any {
        if (row.hasOwnProperty(this.dimensionMetaField)){
            return row.get(this.dimensionMetaField);
        }else{
            return row.get(this.dimensionMetaField.replace("_date", "_time"));
        }
    }

    getMeasureValue(row:any) : any {
        if (row.hasOwnProperty(this.measureField)){
            return row.get(this.measureField);
        }else{
            return row.get(this.measureField.replace("_date", "_time"));
        }
    }

    getColorValue(row:any) : any {
        return this.colorField !== '' ? row.get(this.colorField) : null;
    }

    convertToTitle(str: string) : string {
        return str.replace(/-|_/gi, ' ').split(' ')
            .map(w => w[0].toUpperCase() + w.substr(1).toLowerCase())
            .join(' ');
    }
}

// Default provied uses the name field as both the value and meta.
//
class DefaultDimensionDataProvider extends BaseDimensionDataProvider {
    constructor(dimensionConfig: IDimension, measureConfig: IMeasure, dataFrame: any) {
        super(dimensionConfig, measureConfig, null, dataFrame);

        if(isLookupProperty(dimensionConfig.source.item)) {
            this.dimensionField = `${dimensionConfig.source.item!.name}_name`;
        } else {
            this.dimensionField = `${dimensionConfig.source.item!.name}_value`;
        }
             
        this.dimensionMetaField = this.dimensionField;
        
        if(isStats(measureConfig.source.item)) {
            this.measureField = `${measureConfig.source.item!.name}_${measureConfig.source.sub}`;
        } else {
            this.measureField = `${measureConfig.source.item!.name}_${measureConfig.aggregation}`;
        }
    }
}

// provider for dimensions that are of an entity. the enitiy id is
// used is the meta value and this will assist in creating filters for
// selected items.
//
class EntityTypeDimensionDataProvider extends BaseDimensionDataProvider {
    constructor(dimensionConfig: IDimension, measureConfig: IMeasure, dataFrame: any) {
        super(dimensionConfig, measureConfig, null, dataFrame);
        console.log("dimensionConfig", dimensionConfig);
        console.log("measureConfig", measureConfig);
        //console.log("dataFrame", dataFrame);
        // set dimension fields
        if(isEntity(dimensionConfig.source.item)) {
            this.dimensionField = `${dimensionConfig.source.item!.name}_name`;
            this.dimensionMetaField = `${dimensionConfig.source.item!.name}_id`;
        } else {
            const prop = dimensionConfig.source.item as Property;

            this.dimensionField = `${prop.property}_name`;
            this.dimensionMetaField = `${prop.property}_id`;
        }

        // set measure fields
        if(isStats(measureConfig.source.item)) {
            const prop = measureConfig.source.item as Property;
            this.measureField = `${prop.property}_${measureConfig.source.sub}`;
        } else {
            const prop = measureConfig.source.item as Property;
            this.measureField = `${prop!.property}_${measureConfig.aggregation}`;
        }
    }
}

// This will handle time based dimensions. The value will be parsed to a string date in the form YYYY-MM-DD.
//
class PropertyTimeDimensionDataProvider extends BaseDimensionDataProvider {
    constructor(dimensionConfig: IDimension, measureConfig: IMeasure, dataFrame: any) {
        super(dimensionConfig, measureConfig, null, dataFrame);

        if(isStats(measureConfig.source.item)) {
            this.measureField = `${measureConfig.source.item!.name}_${measureConfig.source.sub}`;
        } else {
            this.measureField = `${measureConfig.source.item!.name}_${measureConfig.aggregation}`;
        }
    }

    // 
    getDimensionValue(row:any) : any {
        if(this.dimensionConfig.bin === 'year') {
            const yearField = `${this.dimensionConfig.source.item.name}_year`;
            const yearValue = row.get(yearField);

            return moment(`${yearValue}-1-1`).format('YYYY-MM-DD');

        } else if (this.dimensionConfig.bin === 'month,year') {
            const yearField = `${this.dimensionConfig.source.item.name}_year`;
            const monthField = `${this.dimensionConfig.source.item.name}_month_in_year`;
            const yearValue = row.get(yearField);
            const monthValue = row.get(monthField);
            
            return moment(`${yearValue}-${monthValue}-1`).format('YYYY-MM-DD');

        } else if(this.dimensionConfig.bin === 'date') {
            const field = `${this.dimensionConfig.source.item.name}_date`;
            const value = row.get(field);
            return value;
        } else if(this.dimensionConfig.bin === 'year,week') {
            const yearField = `${this.dimensionConfig.source.item.name}_year`;
            const weekField = `${this.dimensionConfig.source.item.name}_week`;
            const yearValue = row.get(yearField);
            const weekValue = row.get(weekField);

            return moment(`${yearValue}-01-02T00:00:00Z`).day("Monday").week(weekValue).format('YYYY-MM-DD');
        }
        
        return new Date(Date.parse(row.get(this.dimensionField)));
    }

    getDimensionMetaValue(row:any) : any {
        return this.getDimensionValue(row);
    }

    dimensionTitle(): string {
        if(this.dimensionConfig.bin === 'year') {
            return `${this.convertToTitle(this.dimensionConfig.source.item.name)} Yearly`;
        } else if (this.dimensionConfig.bin === 'month,year') {
            return `${this.convertToTitle(this.dimensionConfig.source.item.name)} Monthly`;
        } else if(this.dimensionConfig.bin === 'date') {
            return `${this.convertToTitle(this.dimensionConfig.source.item.name)} Daily`;
        }
        else if(this.dimensionConfig.bin === 'year,week') {
            return `${this.convertToTitle(this.dimensionConfig.source.item.name)} Weekly`;
        }

        return '';
    }
}

class PropertyDataProvider extends BaseDimensionDataProvider {
    constructor(measureXConfig: IMeasure, measureYConfig: IMeasure, colorConfig: IMeasure, dataFrame: any) {
        super(measureXConfig, measureYConfig, colorConfig, dataFrame);

        const xProp = measureXConfig.source.item as Property;
        const yProp = measureYConfig.source.item as Property;
        const colorProp = colorConfig ? colorConfig.source.item as Property : null;

        // the query creator will use the property name to name the expected results.
        this.dimensionField = `${xProp.property}_${measureXConfig.source.sub}`;
        this.measureField = `${yProp.property}_${measureYConfig.source.sub}`;
        this.colorField = colorProp ? `${colorProp.property}_${colorConfig.source.sub}` : '';

        this.dimensionMetaField = this.dimensionField;
    }
}

export class StatsTimeSeriesDataProvider extends BaseDimensionDataProvider {
    dateDimensionConfig: IDimension;

    constructor(dimensionConfig: IDimension, dateDimensionConfig: IDimension, measureConfig: IMeasure, dataFrame: any) {
        super(dimensionConfig, measureConfig, null, dataFrame);

        this.dateDimensionConfig = dateDimensionConfig;

        this.dimensionField = `${dimensionConfig.source.item!.name}_name`;
        this.dimensionMetaField = `${dimensionConfig.source.item!.name}_id`;

        if(isStats(measureConfig.source.item)) {
            this.measureField = `${measureConfig.source.item!.name}_${measureConfig.source.sub}`;
        } else {
            this.measureField = `${measureConfig.source.item!.name}_${measureConfig.aggregation}`;
        }
    }

    dimensionTitle(): string {
        console.log('dateDimensionConfig', this.dateDimensionConfig);

        if(this.dateDimensionConfig.bin === 'year') {
            return `${this.convertToTitle(this.dateDimensionConfig.source.item!.name)} Yearly`;
        } else if (this.dateDimensionConfig.bin === 'month') {
            return `${this.convertToTitle(this.dateDimensionConfig.source.item!.name)} Monthly`;
        } else if(this.dateDimensionConfig.bin === 'date') {
            return `${this.convertToTitle(this.dateDimensionConfig.source.item!.name)} Daily`;
        }
        else if(this.dateDimensionConfig.bin === 'week') {
            return `${this.convertToTitle(this.dateDimensionConfig.source.item!.name)} Weekly`;
        }

        return '';
    }
}


// Factory to get a plot data provider.
//
export function getPlotDataProvider(dimensionConfig: any, measureConfig: any, dataFrame: any, colorConfig: any = null): IDataProvider {
    if(dimensionConfig) {
        if(isEntity(dimensionConfig.source.item)) {
            return new EntityTypeDimensionDataProvider(dimensionConfig, measureConfig, dataFrame);
        } else if(isTimestampProperty(dimensionConfig.source.item)) {
            return new PropertyTimeDimensionDataProvider(dimensionConfig, measureConfig, dataFrame);
        } 

        return new DefaultDimensionDataProvider(dimensionConfig, measureConfig, dataFrame); 
    }

    // use for scatter plots with 2 measures
    return new PropertyDataProvider(measureConfig[0], measureConfig[1], colorConfig, dataFrame); 
}